import { TaskAPI } from 'api/tasks';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { mergeTaskFields, mergeTasksIntoCollection, taskCollection } from './task-collection';
/**
 * Centralized READ wrappers over `TaskAPI`. Consumers should call these instead
 * of `TaskAPI.*` directly so every fetched `Task` flows through
 * `taskCollection.push(...)` — keeping the collection the single source of
 * truth — while the original API result is returned unchanged.
 *
 * Each wrapper forwards the exact arguments to its `TaskAPI` method (signatures
 * are derived from `TaskAPI` via `Parameters<>` so they stay in lock-step) and
 * preserves the original return type.
 */ /** Narrows an unknown-ish value to `Task[]` for the push-if-array cases. */ const isTaskArray = (value)=>Array.isArray(value);
/**
 * Per-task refetch generation. Rapid task edits (e.g. three quick status
 * changes) each emit a WS update that triggers `fetchTask`. Those refetches can
 * resolve out of order, and the server has no monotonic version field to dedupe
 * by, so a late-resolving STALE refetch would otherwise push an old snapshot
 * and flicker the UI. We stamp each `fetchTask` call with an incrementing
 * generation and only push the result if it is still the latest in-flight
 * refetch for that task — older results are dropped (their server snapshot is
 * by definition staler than the newest refetch).
 */ const taskFetchGen = new Map();
/** GET project task list → push the returned tasks. */ export const fetchTaskList = async (...args)=>{
    const result = await TaskAPI.getTaskList(...args);
    taskCollection.push(result, {
        source: 'fetch'
    });
    demoteHydratedTasks(result);
    return result;
};
/**
 * Gantt-light fidelity registry: marked id ⇒ full-payload entry. Light
 * merges preserve, trimmed replace-pushes demote, hydration skips marked.
 */ const hydratedTaskIds = new Set();
export const isTaskHydrated = (taskId)=>hydratedTaskIds.has(taskId);
const demoteHydratedTasks = (tasks)=>{
    tasks === null || tasks === void 0 ? void 0 : tasks.forEach((task)=>hydratedTaskIds.delete(task.taskId));
};
/**
 * Latest hydrate-overlay write per id. Overlays carry no light fields, so
 * the light merge may proceed over one — but only when prevRealSeq (last
 * non-overlay write, carried across chained overlays) predates the request.
 */ const hydrateOverlayStamps = new Map();
/** Per-project reset epoch: hydrate responses that predate a reset must not
 *  re-mark their ids. */ const hydrationResetEpochs = new Map();
const hydrationResetEpoch = (projectId)=>{
    var _hydrationResetEpochs_get;
    return (_hydrationResetEpochs_get = hydrationResetEpochs.get(projectId)) !== null && _hydrationResetEpochs_get !== void 0 ? _hydrationResetEpochs_get : 0;
};
/** Reconnect recovery: force the visible rows to re-hydrate fresh. */ export const resetHydratedTasksForProject = (projectId)=>{
    hydrationResetEpochs.set(projectId, hydrationResetEpoch(projectId) + 1);
    taskCollection.getByIndex('byProject', projectId).forEach((task)=>hydratedTaskIds.delete(task.taskId));
};
const lightLoadedProjects = new Set();
/** True once the light gantt list has loaded for this project this session. */ export const hasGanttLightTaskListLoaded = (projectId)=>lightLoadedProjects.has(projectId);
/** Latest light fetch per project — stale overlapping responses are dropped
 *  (an old response merging after a newer one could resurrect deletions). */ const lightFetchGen = new Map();
export const fetchGanttLightTaskList = async (projectId)=>{
    var _lightFetchGen_get;
    const gen = ((_lightFetchGen_get = lightFetchGen.get(projectId)) !== null && _lightFetchGen_get !== void 0 ? _lightFetchGen_get : 0) + 1;
    lightFetchGen.set(projectId, gen);
    const requestSeq = taskCollection.getWriteSeq();
    const result = await TaskAPI.getGanttLightTaskList(projectId);
    if (lightFetchGen.get(projectId) !== gen) {
        return result;
    }
    // Skip ids written locally after the request left (the response predates
    // them); a pure hydrate-overlay write is exempt — it carries no light
    // fields. Deletes clear write stamps, so mid-flight deletions are blocked
    // by their tombstone. Skipped ids still count for the reconcile below.
    mergeTasksIntoCollection(result.filter((task)=>{
        const id = task.taskId;
        if (taskCollection.getLastDeleteSeq(id) > requestSeq) {
            return false;
        }
        const seq = taskCollection.getLastWriteSeq(id);
        if (seq <= requestSeq) {
            return true;
        }
        const stamp = hydrateOverlayStamps.get(id);
        return (stamp === null || stamp === void 0 ? void 0 : stamp.overlaySeq) === seq && stamp.prevRealSeq <= requestSeq;
    }), {
        source: 'fetch'
    });
    lightLoadedProjects.add(projectId);
    // Entries absent from the response were deleted/revoked server-side.
    // Protection is INSERT-scoped: creates landing mid-flight are kept; value
    // overlays of existing ids don't shield a deletion.
    const returnedIds = new Set(result.map((task)=>task.taskId));
    taskCollection.getByIndex('byProject', projectId).forEach((task)=>{
        const id = task.taskId;
        if (returnedIds.has(id) || taskCollection.getLastInsertSeq(id) > requestSeq || taskCollection.getState(id) !== 'synced') {
            return;
        }
        taskCollection.externalDelete(id);
        hydratedTaskIds.delete(id);
    });
    return result;
};
/** In-flight light fetches, so N cards priming at once issue ONE request. */ const lightInFlight = new Map();
/**
 * Ensure this project's dependency graph is in the collection, fetching the
 * light list only if it isn't already.
 *
 * The paginated plan board loads tasks a lane-page at a time, and those
 * payloads are metaData-stripped — no `dependentTasks`, so no dependency
 * edges. Every consumer that reasons about dependencies (the date-shift
 * cascade and its confirmation preview) therefore saw an empty graph and
 * concluded the edit affected nothing.
 *
 * The light list is the same one the timeline builds its dependency arrows
 * from, so it carries `dependencyType` and `lag` — enough to compute the
 * shifted dates locally rather than wait a round-trip for them.
 *
 * Safe to call from every card on the board: already-loaded projects return
 * immediately, and concurrent callers share one request. Cheap to call
 * speculatively (e.g. when a date picker opens) for the same reason.
 */ export const ensureGanttLightTaskList = async (projectId)=>{
    if (lightLoadedProjects.has(projectId)) {
        return;
    }
    let pending = lightInFlight.get(projectId);
    if (!pending) {
        pending = fetchGanttLightTaskList(projectId).finally(()=>{
            lightInFlight.delete(projectId);
        });
        lightInFlight.set(projectId, pending);
    }
    await pending;
};
/** POST hydrate a bounded id window → push full tasks, mark them hydrated. */ export const fetchGanttHydratedTasks = async (projectId, taskIds)=>{
    // Snapshot before the round-trip: the response must not overwrite ids
    // written while it was in flight (write seq is stamped synchronously).
    const requestSeq = taskCollection.getWriteSeq();
    const requestEpoch = hydrationResetEpoch(projectId);
    const result = await TaskAPI.hydrateGanttTasks(projectId, taskIds);
    return processHydrateResponse(projectId, taskIds, result, requestSeq, requestEpoch);
};
const processHydrateResponse = (projectId, taskIds, result, requestSeq, requestEpoch)=>{
    // Started before a hydration reset — push value-guarded data, but leave
    // every id unmarked so the post-reset re-hydration still happens.
    const canMarkHydrated = requestEpoch === hydrationResetEpoch(projectId);
    const returnedIds = new Set();
    const toPush = [];
    result.forEach((task)=>{
        var _task_fields, _task_fieldsAccessMetaInBase3, _task_projectFinancialsBudgets;
        const id = task.taskId;
        returnedIds.add(id);
        const state = taskCollection.getState(id);
        // Deleted mid-flight — don't resurrect, leave unmarked.
        if (state === undefined) {
            return;
        }
        // Stale for this id (newer local write / in-flight mutation) — skip,
        // leave unmarked so the next settle re-fetches.
        if (taskCollection.getLastWriteSeq(id) > requestSeq || state !== 'synced') {
            return;
        }
        const existing = taskCollection.get(id);
        if (!existing) {
            return;
        }
        // Heavy-fields-only overlay: light fields stay owned by the light
        // list/WS/edits, so a stale snapshot can never regress them. Clearable
        // heavies copy WITHOUT fallback — absence in a full-fidelity response is
        // an authoritative clear (key present ⇒ hydrated, even if undefined).
        toPush.push({
            ...existing,
            fields: (_task_fields = task.fields) !== null && _task_fields !== void 0 ? _task_fields : existing.fields,
            fieldsAccessMetaInBase3: (_task_fieldsAccessMetaInBase3 = task.fieldsAccessMetaInBase3) !== null && _task_fieldsAccessMetaInBase3 !== void 0 ? _task_fieldsAccessMetaInBase3 : existing.fieldsAccessMetaInBase3,
            linkedResources: task.linkedResources,
            currentSprint: task.currentSprint,
            associatedSprints: task.associatedSprints,
            epic: task.epic,
            startDateActual: task.startDateActual,
            projectFinancialsBudgets: (_task_projectFinancialsBudgets = task.projectFinancialsBudgets) !== null && _task_projectFinancialsBudgets !== void 0 ? _task_projectFinancialsBudgets : existing.projectFinancialsBudgets,
            formInstanceId: task.formInstanceId,
            // Not in the light projection — hydration is their delivery path.
            completedAt: task.completedAt,
            atRisk: task.atRisk,
            effort: task.effort,
            taskEffortSetting: task.taskEffortSetting,
            timeEntryMinutes: task.timeEntryMinutes,
            totalAllocatedMinutes: task.totalAllocatedMinutes,
            remainingEffortInMinutes: task.remainingEffortInMinutes,
            defaultTimeEntryBillable: task.defaultTimeEntryBillable,
            defaultTimeEntryCategory: task.defaultTimeEntryCategory,
            isMilestoneRevenueRecognitionTask: task.isMilestoneRevenueRecognitionTask,
            milestoneTaskRevenueRecognitionAmount: task.milestoneTaskRevenueRecognitionAmount
        });
        if (canMarkHydrated) {
            hydratedTaskIds.add(id);
        }
    });
    // Pre-push seq = last real write (carried across chained overlays).
    const prePushSeqs = new Map();
    toPush.forEach((task)=>prePushSeqs.set(task.taskId, taskCollection.getLastWriteSeq(task.taskId)));
    taskCollection.push(toPush, {
        source: 'fetch'
    });
    toPush.forEach((task)=>{
        var _prePushSeqs_get;
        const id = task.taskId;
        const prevSeq = (_prePushSeqs_get = prePushSeqs.get(id)) !== null && _prePushSeqs_get !== void 0 ? _prePushSeqs_get : 0;
        const prior = hydrateOverlayStamps.get(id);
        hydrateOverlayStamps.set(id, {
            overlaySeq: taskCollection.getLastWriteSeq(id),
            prevRealSeq: (prior === null || prior === void 0 ? void 0 : prior.overlaySeq) === prevSeq ? prior.prevRealSeq : prevSeq
        });
    });
    // Requested ids the server didn't return (deleted / visibility revoked)
    // would re-request on every settle forever; treat as terminally hydrated.
    taskIds.forEach((id)=>{
        // Same guards as returned entries — marking a stale id blocks re-hydration.
        if (canMarkHydrated && !returnedIds.has(id) && taskCollection.getState(id) === 'synced' && taskCollection.getLastWriteSeq(id) <= requestSeq) {
            hydratedTaskIds.add(id);
        }
    });
    return result;
};
/**
 * GET a single task (wraps `getTask`) → push `[result]`, return the `Task`.
 *
 * Only the latest in-flight refetch for a given task pushes into the
 * collection; older, out-of-order responses are dropped to avoid flicker (see
 * `taskFetchGen`). The result is always returned to the caller regardless.
 */ export const fetchTask = async (...args)=>{
    var _taskFetchGen_get;
    const [, taskId] = args;
    const gen = ((_taskFetchGen_get = taskFetchGen.get(taskId)) !== null && _taskFetchGen_get !== void 0 ? _taskFetchGen_get : 0) + 1;
    taskFetchGen.set(taskId, gen);
    const result = await TaskAPI.getTask(...args);
    if (taskFetchGen.get(taskId) === gen) {
        taskCollection.push([
            result
        ], {
            source: 'fetch'
        });
        hydratedTaskIds.add(taskId);
    }
    return result;
};
/**
 * Push a PARTIAL task response onto the existing collection record.
 *
 * The detail view splits a task across two partial endpoints — default fields
 * and additional fields — neither of which is the whole task. A plain (replace)
 * push of one would drop the fields the other carries, AND clobber an
 * optimistic edit (e.g. a just-added assignee) that lands between the two
 * fetches. Merge mode (`{ ...existing, ...incoming }`) layers the partial view
 * on top of absent keys — but `fields` needs deeper care: get-default-fields
 * returns ONLY the status field in `fields`, so a shallow merge would replace
 * the full custom-field array with that one-element array (emptying every
 * custom-field cell in the grid/gantt). Merge `fields` element-wise by
 * `fieldId` instead — same rule the collection's own `mergeChanges` applies to
 * optimistic field edits.
 */ const pushPartialTask = (result)=>{
    // Synced base (not `get()`): a merge built on the optimistic overlay would
    // write the un-committed value into the backing store (see
    // `mergeTasksIntoCollection`).
    const existing = taskCollection.getSynced(result.taskId);
    if (existing && result.fields) {
        taskCollection.push([
            {
                ...result,
                fields: mergeTaskFields(existing.fields, result.fields)
            }
        ], {
            source: 'fetch',
            mode: 'merge'
        });
        return;
    }
    taskCollection.push([
        result
    ], {
        source: 'fetch',
        mode: 'merge'
    });
};
/** GET a task's default fields → merge onto the existing record (see above). */ export const fetchTaskDefaultFields = async (...args)=>{
    var // The default-fields payload embeds this task's subtasks (taskId, name,
    // parentTask, project, status). Explode them into the collection so the
    // drawer's subtask table can read them via the `byParent` index WITHOUT a
    // separate full-project `fetchTaskList`. Merge mode + element-wise field
    // merge (via pushPartialTask) preserves any richer subtask snapshot already
    // present (e.g. dates from a prior fetch) and never clobbers a subtask's
    // custom-field cells with the status-only `fields` array.
    _result_subTasks;
    const result = await TaskAPI.getTaskDetailsDefaultFields(...args);
    pushPartialTask(result);
    ((_result_subTasks = result.subTasks) !== null && _result_subTasks !== void 0 ? _result_subTasks : []).forEach((subTask)=>{
        if ((subTask === null || subTask === void 0 ? void 0 : subTask.taskId) != null) {
            pushPartialTask(subTask);
        }
    });
    return result;
};
/** GET a task's additional fields → merge onto the existing record (see above). */ export const fetchTaskAdditionalFields = async (...args)=>{
    const result = await TaskAPI.getTaskDetailsAdditionalFields(...args);
    pushPartialTask(result);
    return result;
};
/** GET subtasks of a task → lite DTO; merge (no-downgrade) the returned tasks. */ export const fetchSubTasks = async (...args)=>{
    const result = await TaskAPI.getSubTasks(...args);
    mergeTasksIntoCollection(result, {
        source: 'fetch'
    });
    demoteHydratedTasks(result);
    return result;
};
/** POST bulk-task-details by ids → lite DTO; merge (no-downgrade). WS refetch path — a replace here stomps an open drawer. */ export const fetchBulkTaskDetails = async (...args)=>{
    const result = await TaskAPI.getBulkTaskDetails(...args);
    mergeTasksIntoCollection(result, {
        source: 'fetch'
    });
    demoteHydratedTasks(result);
    return result;
};
/**
 * Fetch an ID-scoped task list with the field-access metadata required by grid
 * editors. Searchlight includes that metadata; getTaskByIds does not.
 */ export const fetchTaskDetailsByIdsWithAccess = async (projectId, taskIds)=>{
    const response = await TaskAPI.getPaginatedTaskListWithProject({
        startRow: 0,
        endRow: 500,
        filter: [],
        filterType: QueryCombinerType.All,
        projectId,
        searchValue: `task*taskId.oneOf=${taskIds.join(',')}`
    });
    const result = response.data;
    mergeTasksIntoCollection(result, {
        source: 'fetch'
    });
    demoteHydratedTasks(result);
    return result;
};
/**
 * GET tasks & milestones for a project. `getTasksAndMilestonesForProjects`
 * resolves to `Task | Task[]` (the transformer's default generic), so we only
 * push when the result is an array.
 */ export const fetchTasksAndMilestones = async (...args)=>{
    const result = await TaskAPI.getTasksAndMilestonesForProjects(...args);
    if (isTaskArray(result)) {
        mergeTasksIntoCollection(result, {
            source: 'fetch'
        });
        demoteHydratedTasks(result);
    }
    return result;
};
/** GET paginated task list → push `data`. */ export const fetchPaginatedTaskList = async (...args)=>{
    const result = await TaskAPI.getPaginatedTaskList(...args);
    mergeTasksIntoCollection(result.data, {
        source: 'fetch'
    });
    demoteHydratedTasks(result.data);
    return result;
};
/** GET paginated task list scoped to a project → push `data`. */ export const fetchPaginatedTaskListWithProject = async (...args)=>{
    const result = await TaskAPI.getPaginatedTaskListWithProject(...args);
    // Searchlight rows are partial and omit detail-only fields (e.g.
    // `attachments`, `subTasks`). Merge them so opening a task from an email
    // deep-link — which loads the richer task detail first — cannot downgrade
    // the shared collection entry and drop attachments a few seconds later
    // when the grid fetch resolves. Same rule as `fetchPaginatedTaskList` and
    // `postPaginatedTasksWithProject` (TASK-1000001031779).
    mergeTasksIntoCollection(result.data, {
        source: 'fetch'
    });
    demoteHydratedTasks(result.data);
    return result;
};
/** GET paginated recently-assigned tasks → lite DTO; merge `data`. */ export const fetchRecentlyAssignedTasks = async (...args)=>{
    const result = await TaskAPI.getPaginatedRecentlyAssignedTaskList(...args);
    mergeTasksIntoCollection(result.data, {
        source: 'fetch'
    });
    demoteHydratedTasks(result.data);
    return result;
};
/** POST paginated task list scoped to a project → push `data`. */ export const postPaginatedTasksWithProject = async (...args)=>{
    const result = await TaskAPI.postPaginatedTaskListWithProject(...args);
    // Searchlight rows are partial and omit hierarchy such as `subTasks`.
    // Merge them so visiting the task list cannot downgrade the richer gantt
    // entries already held by the shared collection.
    mergeTasksIntoCollection(result.data, {
        source: 'fetch'
    });
    return result;
};
/** POST filtered tasks → lite DTO; merge `data`. */ export const postFilteredTasks = async (...args)=>{
    const result = await TaskAPI.postFilteredTasks(...args);
    mergeTasksIntoCollection(result.data, {
        source: 'fetch'
    });
    demoteHydratedTasks(result.data);
    return result;
};
/** GET all tasks scoped to a project (searchlight) → lite DTO; merge `data`. */ export const fetchAllTasksWithProject = async (...args)=>{
    const result = await TaskAPI.getAllTaskListWithProject(...args);
    mergeTasksIntoCollection(result.data, {
        source: 'fetch'
    });
    demoteHydratedTasks(result.data);
    return result;
};
/** GET paginated tasks for a dashboard widget → lite DTO; merge `data`. */ export const fetchDashboardTasks = async (...args)=>{
    const result = await TaskAPI.getPaginatedTaskListForDashbaord(...args);
    mergeTasksIntoCollection(result.data, {
        source: 'fetch'
    });
    demoteHydratedTasks(result.data);
    return result;
};
/**
 * POST paginated task list count. Count-only response (no `Task` entities) —
 * wrapped purely for centralization; nothing to push.
 */ export const fetchTaskCount = (...args)=>TaskAPI.postPaginatedTaskListCount(...args);
/** GET task search → lite DTO; merge the returned tasks. */ export const searchTasks = async (...args)=>{
    const result = await TaskAPI.searchTasks(...args);
    mergeTasksIntoCollection(result, {
        source: 'fetch'
    });
    demoteHydratedTasks(result);
    return result;
};
/** GET timesheet task search → lite DTO; merge the returned tasks. */ export const searchTasksForTimesheet = async (...args)=>{
    const result = await TaskAPI.searchTasksForTimesheet(...args);
    mergeTasksIntoCollection(result, {
        source: 'fetch'
    });
    demoteHydratedTasks(result);
    return result;
};
/**
 * GET subtask count for a project. No `Task` entities — wrapped purely for
 * centralization.
 */ export const fetchSubtaskCount = (...args)=>TaskAPI.getSubtaskCount(...args);
/**
 * POST unique field values for bulk editing. No `Task` entities — wrapped
 * purely for centralization.
 */ export const fetchTaskFieldValues = (...args)=>TaskAPI.getTaskFieldValues(...args);
/**
 * GET the account-level task field definitions. No `Task` entities — wrapped
 * purely for centralization.
 */ export const fetchTaskFields = (...args)=>TaskAPI.getTaskFields(...args);
/** Async CSV export for grouped task views. Nothing to push. */ export const exportTasksGrouped = (...args)=>TaskAPI.exportAsCSVAsyncForGroupType(...args);
/** Async CSV export for non-grouped task views. Nothing to push. */ export const exportTasksNonGrouped = (...args)=>TaskAPI.exportAsCSVAsyncForNonGroup(...args);
/** Async CSV export (V2) for grouped task views. Nothing to push. */ export const exportTasksGroupedV2 = (...args)=>TaskAPI.exportAsCSVAsyncForGroupTypeV2(...args);
/** Async CSV export (V2) for non-grouped task views. Nothing to push. */ export const exportTasksNonGroupedV2 = (...args)=>TaskAPI.exportAsCSVAsyncForNonGroupV2(...args);
/** Fire-and-forget filter logging endpoint. Nothing to push. */ export const logTaskFilters = (...args)=>TaskAPI.logFilters(...args);
/** Refetch full task detail + field slices into the collection. */ export const refetchTaskInCollection = async (projectId, taskId)=>{
    await Promise.all([
        fetchTask(projectId, taskId),
        fetchTaskDefaultFields({
            projectId,
            taskId
        }),
        fetchTaskAdditionalFields({
            projectId,
            taskId
        })
    ]);
    // Every caller reaches here AFTER a local write (subtask create, jira link,
    // form submit, attachment version) to reconcile the fields the server
    // recomputed — `subTaskOrder` and the subtask count the plan card renders,
    // most visibly. The three fetches above are reads and push silently, so
    // without this the ACTING tab updated and every other tab kept the old
    // count. Re-emit the reconciled record once, attributed to the mutation
    // that triggered the refetch, so peers receive it too.
    const reconciled = taskCollection.getSynced(taskId);
    if (reconciled) {
        taskCollection.push([
            reconciled
        ], {
            source: 'mutation'
        });
    }
};
